import React, { useCallback } from 'react'

import classnames from 'classnames'
import {
  BoostFundedGuestOffer,
  useSaveOfferToWalletMutation
} from 'src/apollo/onlineOrdering'
import { AuthenticationSource } from 'public/components/default_template/online_ordering/checkout/checkoutUtils'
import { PwlessAuth } from 'public/components/default_template/online_ordering/account/pwlessAuth/PwlessAuth'

import {
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  useModal
} from 'shared/components/common/modal'
import { useOOClient } from 'shared/components/common/oo_client_provider/OOClientProvider'
import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'

import {
  FreeDeliveryIllustration,
  StickerIllustration,
  TagIllustration
} from 'public/components/default_template/offers/OffersIcons'
import { t } from 'src/translations'

import { DiscountType } from './types'
import { useStyledCardColors } from './useStyledCardColors'
import { useCustomer } from 'public/components/online_ordering/CustomerContextCommon'
import Button from 'src/shared/components/common/button'
import { alertError, alertSuccess } from 'src/shared/js/alertUtils'

type BoostedOfferProps = {
  offer: BoostFundedGuestOffer
  illustrationType: 'sticker' | 'tag' | 'freeDelivery'
  illustrationColor: string
  onWalletChange: () => Promise<any>
}

export const BoostedOffer = ({
  offer,
  illustrationColor,
  illustrationType,
  onWalletChange
}: BoostedOfferProps) => {
  const { customer } = useCustomer()
  const {
    isOpen: isAuthModalOpen,
    onClose: onAuthModalClose,
    onOpen: onAuthModalOpen
  } = useModal()
  const { ooRestaurant } = useRestaurant()
  const { backgroundColor, textColor, borderColor, templateOverrides } =
    useStyledCardColors()

  const ooClient = useOOClient()
  const [saveToWallet, { loading }] = useSaveOfferToWalletMutation({
    client: ooClient
  })

  const { isOpen, onClose, onOpen } = useModal()

  // The display text is autogenerated based on the offer type, so we have to do some sketchy string parsing here
  const discountType = offer.displayText?.toLowerCase()?.includes('%')
    ? DiscountType.PERCENT
    : DiscountType.DOLLAR

  const illustration =
    illustrationType === 'freeDelivery' ? (
      <FreeDeliveryIllustration color={illustrationColor} />
    ) : illustrationType === 'tag' ? (
      <TagIllustration color={illustrationColor} discountType={discountType} />
    ) : (
      <StickerIllustration
        color={illustrationColor}
        discountType={discountType}
      />
    )

  const onSave = useCallback(async () => {
    try {
      const result = await saveToWallet({
        variables: {
          input: {
            offerId: offer.id,
            offerType: offer.type,
            restaurantId: ooRestaurant?.guid
          }
        }
      })

      if (
        result?.data?.guestOffers?.saveOfferToWallet?.__typename ===
        'SaveOfferToWalletSuccess'
      ) {
        alertSuccess(t('boost.offer-successfully-saved'), {
          containerId: 'giftCardPromoCodeToastContainer'
        })
      } else {
        alertError(t('boost.offer-save-failure'), {
          containerId: 'giftCardPromoCodeToastContainer'
        })
      }
    } catch (e) {
      alertError(t('boost.offer-save-failure'), {
        containerId: 'giftCardPromoCodeToastContainer'
      })
    } finally {
      await onWalletChange()
      onClose()
    }
  }, [offer, saveToWallet, onWalletChange, onClose, ooRestaurant?.guid])

  return (
    <>
      <button
        className={classnames(
          'styledCard',
          templateOverrides.borderStroke,
          templateOverrides?.roundedCorner,
          templateOverrides?.dropShadow
        )}
        style={{
          borderColor,
          color: textColor,
          background: `linear-gradient(270deg, ${illustrationColor}22 5%, ${backgroundColor} 25%)`
        }}
        onClick={customer ? onOpen : onAuthModalOpen}
        data-testid={`offer-${offer.id}`}
      >
        <div className={classnames('cardContent')}>
          <div className='cardInfo'>
            <div className='cardTitleGroup'>
              <div className='cardText' data-testid='offer-text'>
                {offer.displayText}
              </div>
            </div>
            <div className='cardDetails'>
              {customer
                ? t('boost.save-to-wallet')
                : t('boost.sign-in-to-save')}
            </div>
          </div>
          <div
            className='illustration'
            data-testid={`offer-illustration-${illustrationType}-${discountType}`}
          >
            {illustration}
          </div>
        </div>
      </button>
      <Modal isOpen={isOpen} onClose={onClose} className='offerModalWrapper'>
        <ModalOverlay fadeIn fadeOut />
        <ModalContent contentClassName='offerModal'>
          <ModalCloseButton />
          <div className='content' data-testid='offer-modal'>
            <div
              className='header'
              style={{
                background: `linear-gradient(0deg, white, ${illustrationColor}22)`
              }}
            >
              {illustration}
              <div className='title'>{offer.displayText}</div>
              {offer.offerDescription && (
                <div className='description'>{offer.offerDescription}</div>
              )}
              {offer.diningOptionBehaviors
                .map((opt) => {
                  switch (opt) {
                    case 'DINE_IN':
                      return t('rx-search.dine-in')
                    case 'DELIVERY':
                      return t('rx-config.delivery')
                    case 'TAKE_OUT':
                      return t('rx-config.takeout')
                    default:
                      return ''
                  }
                })
                .filter(Boolean)
                .join(', ')}
              <Button disabled={loading} onClick={onSave}>
                {t('boost.save-to-wallet')}
              </Button>
            </div>
          </div>
        </ModalContent>
      </Modal>
      <Modal
        isOpen={isAuthModalOpen}
        onClose={onAuthModalClose}
        className='authModalWrapper'
      >
        <ModalOverlay fadeIn fadeOut />
        <PwlessAuth
          source={AuthenticationSource.SegmentedOfferCard}
          onSuccessfulLogin={onAuthModalClose}
        />
      </Modal>
    </>
  )
}
